import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  Form,
  Row,
  Col,
  FormGroup,
  Button,
  Container
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages
} from "@whitespace/whitespace-ui";
import { format } from "date-fns";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import AmplifyError from "components/AmplifyError/AmplifyError";
import ISaveRegionRequest from "interfaces/Request/ISaveRegionRequest";
import regionSchema from "modules/schemas/regionSchema";
import "styles/form.scss";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import { findDuplicates } from "helpers/numberUtils";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddRegion = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [isDuplicateTimeError, setIsDuplicateTimeError] = useState<boolean>(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<ISaveRegionRequest>({
    resolver: yupResolver(regionSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [statusReportTimeOneRef, setStatusReportTimeOneRef] = useState<
  TimePickerComponent | undefined
  >();
  const [statusReportTimeTwoRef, setStatusReportTimeTwoRef] = useState<
  TimePickerComponent | undefined
  >();
  const [statusReportTimeThreeRef, setStatusReportTimeThreeRef] = useState<
  TimePickerComponent | undefined
  >();
  const [statusReportTimeFourRef, setStatusReportTimeFourRef] = useState<
  TimePickerComponent | undefined
  >();

  const navigate = useNavigate();

  const {
    isSending,
    errors: apiErrors,
    data,
    sendRequest,
    statusCode
  } = useAmplifyREST();

  const handleNavToSettings = () => {
    navigate(RoutePathsEnum.Settings);
  };

  const handleNavToRegions = () => {
    navigate(RoutePathsEnum.SettingsRegions);
  };

  const isTimeComponentSet = (
    timeComponent: TimePickerComponent | undefined
  ): boolean => !!(timeComponent && timeComponent.value);

  const canFormBeSubmitted = () => {
    let statusReportCount = 0;

    if (isTimeComponentSet(statusReportTimeOneRef)) statusReportCount++;

    if (isTimeComponentSet(statusReportTimeTwoRef)) statusReportCount++;

    if (isTimeComponentSet(statusReportTimeThreeRef)) statusReportCount++;

    if (isTimeComponentSet(statusReportTimeFourRef)) statusReportCount++;

    setAddButtonDisabled(
      !(getValues("name").length > 0) || statusReportCount < 2
    );
  };

  const getStatusReports = (): string[] => {
    setIsDuplicateTimeError(false);

    const statusReportTimes: string[] = [];

    if (isTimeComponentSet(statusReportTimeOneRef)) {
      statusReportTimes.push(
        `1970-01-01T${format(statusReportTimeOneRef!.value, "HH:mm")}`
      );
    }

    if (isTimeComponentSet(statusReportTimeTwoRef)) {
      statusReportTimes.push(
        `1970-01-01T${format(statusReportTimeTwoRef!.value, "HH:mm")}`
      );
    }

    if (isTimeComponentSet(statusReportTimeThreeRef)) {
      statusReportTimes.push(
        `1970-01-01T${format(statusReportTimeThreeRef!.value, "HH:mm")}`
      );
    }

    if (isTimeComponentSet(statusReportTimeFourRef)) {
      statusReportTimes.push(
        `1970-01-01T${format(statusReportTimeFourRef!.value, "HH:mm")}`
      );
    }

    const duplicates = findDuplicates(statusReportTimes);

    if (duplicates.length >= 1) {
      setIsDuplicateTimeError(true);
      return [];
    }

    return statusReportTimes;
  };

  const handleCreateRegionClick = (request: ISaveRegionRequest) => {
    const statusReports = getStatusReports();

    if (statusReports.length > 0) {
      request.statusReportTimes = statusReports;
      sendRequest("settings/regions", "POST", request);
    }
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    if (data && statusCode === 200) handleNavToRegions();
  }, [data, statusCode]);

  useEffect(() => {
    if (statusCode === 401) navigate(RoutePathsEnum.Unauthorised);
  }, [statusCode]);

  useEffect(() => {
    if (apiErrors) setShowExceptions(true);
  }, [apiErrors]);

  const renderStatusReportTime = (idx: number) => (
    <Row className="mb-3 mt-3" key={`statusReportTime_${idx}`}>
      <div className="w-auto">
        <FormGroup as={Row} className="mb-6 d-flex w-50" controlId={`reportTime${idx}`}>
          <Form.Label
            id={`reportTime${idx}Label`}
            className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
          >
            {`Report ${idx + 1} Time:`}
          </Form.Label>

          <div className="ml-1 mt-1">
            <TimePickerComponent
              ref={(t: TimePickerComponent) => {
                // eslint-disable-next-line no-param-reassign

                switch (idx) {
                  case 0: {
                    setStatusReportTimeOneRef(t);
                    break;
                  }
                  case 1: {
                    setStatusReportTimeTwoRef(t);
                    break;
                  }
                  case 2: {
                    setStatusReportTimeThreeRef(t);
                    break;
                  }
                  case 3: {
                    setStatusReportTimeFourRef(t);
                    break;
                  }

                  default: {
                    break;
                  }
                }
              }}
              data-testid={`reportTime${idx}Label`}
              id={`reportTime${idx}`}
              type="text"
              htmlAttributes={{ "aria-labelledby": `reportTime${idx}Label` }}
              format="HH:mm"
              onChange={canFormBeSubmitted}
              showClearButton={false}
            />
          </div>

          <ErrorMessages
            fieldName={`reportTime${idx}`}
            errors={errors}
            validationFailures={null}
          />
        </FormGroup>
      </div>
    </Row>
  );

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />

      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="region-management">
              Add Region
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleCreateRegionClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Home }}
                    onClick={handleNavToSettings}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-regions"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Home }}
                    onClick={handleNavToRegions}
                  >
                    Regions
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-add-region">
                    Add New Region
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>

              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                  data-testid="create"
                  aria-label="create region"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={handleNavToRegions}
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="regions pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <FormGroup as={Row} controlId="name">
                        <Form.Label id="nameLabel" className="horiz-form-label">
                          Region:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="nameLabel"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    <Row className="mb-3 mt-3">
                      <FormGroup
                        as={Row}
                        className="mb-6 d-flex"
                        controlId="active"
                      >
                        <Form.Label
                          id="activeLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Active:
                        </Form.Label>

                        <Form.Check
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-labelledby="activeLabel"
                          defaultChecked
                          aria-required="true"
                          onKeyUp={() => canFormBeSubmitted()}
                          {...register("active")}
                        />

                        <ErrorMessages
                          fieldName="active"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                    {isDuplicateTimeError && (
                      <span
                        data-testid="duplicateTimeException"
                        className="validation-error"
                      >
                        Duplicate Time entry. Cannot be saved.
                      </span>
                    )}
                    <span className="status-report-times">
                      {renderStatusReportTime(0)}

                      {renderStatusReportTime(1)}

                      {renderStatusReportTime(2)}

                      {renderStatusReportTime(3)}
                    </span>
                  </div>
                </Row>
              </Container>
            </section>
          </Form>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default AddRegion;
